import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { subscriptionService } from '../../../services/subscriptionService';
import { supabase } from '../../../lib/supabase';
import analyticsService from '../../../services/analyticsService';
import CancellationModal from './CancellationModal';
import PauseSubscriptionModal from './PauseSubscriptionModal';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';



const PlanManagement = ({ subscription, plans, onPlanChange }) => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showProration, setShowProration] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [addons, setAddons] = useState([]);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [addonQuantity, setAddonQuantity] = useState(1);

  const currentTier = subscription?.tier || 'free';
  const isPaused = subscription?.status === 'paused';

  useEffect(() => {
    if (subscription?.billing_cycle) {
      setBillingCycle(subscription?.billing_cycle);
    }
    loadAddons();
  }, [subscription]);

  const loadAddons = async () => {
    if (!subscription?.user_id) return;
    const { data } = await subscriptionService?.getUserAddons(subscription?.user_id);
    setAddons(data || []);
  };

  const calculateProration = async (plan, cycle) => {
    if (!subscription?.user_id) return null;
    
    const { data } = await subscriptionService?.calculateProration(
      subscription?.user_id,
      plan?.id,
      cycle
    );
    
    return data;
  };

  const handleUpgrade = async (plan, cycle = billingCycle) => {
    if (!plan?.stripe_price_id && !plan?.annual_stripe_price_id) {
      // Enterprise plan - contact sales
      analyticsService?.trackUpgradeCTA('plan_management_enterprise', currentTier);
      return;
    }

    // Calculate proration if changing plans
    if (subscription && subscription?.tier !== 'free' && plan?.tier !== currentTier) {
      const proration = await calculateProration(plan, cycle);
      if (proration) {
        setProrationData({
          plan,
          cycle,
          ...proration
        });
        setShowProration(true);
        return;
      }
    }

    // Proceed with checkout
    await proceedToCheckout(plan, cycle);
  };

  const proceedToCheckout = async (plan, cycle) => {
    setProcessing(true);
    setSelectedPlan(plan?.tier);
    const currentRetry = retryCount;
    const comparison = getPlanComparison(plan?.tier);
    const priceId = cycle === 'annual' ? plan?.annual_stripe_price_id : plan?.stripe_price_id;
    const planPrice = cycle === 'annual' ? plan?.annual_price : plan?.price;
    const currentPrice = plans?.find(p => p?.tier === currentTier)?.price || 0;
    const priceChange = planPrice - currentPrice;

    try {
      const { data, error } = await subscriptionService?.createCheckoutSession(
        priceId,
        subscription?.user_id,
        cycle
      );

      if (error) throw error;

      if (data?.url) {
        // Track subscription change initiation
        analyticsService?.trackSubscriptionChange(
          comparison,
          currentTier,
          plan?.tier,
          priceChange
        );
        
        // If this was a retry, track successful recovery
        if (currentRetry > 0) {
          analyticsService?.trackErrorRecovery(
            'checkout_failed',
            'Checkout succeeded after retry',
            currentRetry,
            true,
            `${currentTier} to ${plan?.tier}`
          );
          setRetryCount(0);
        }
        
        window.location.href = data?.url;
      }
    } catch (error) {
      const newRetryCount = currentRetry + 1;
      setRetryCount(newRetryCount);
      
      console.error('Checkout error:', error);
      
      // Track checkout error
      analyticsService?.trackErrorRecovery(
        'checkout_failed',
        error?.message || 'Failed to start checkout',
        newRetryCount,
        false,
        `${currentTier} to ${plan?.tier}`
      );
      
      // Track friction point
      analyticsService?.trackFrictionPoint(
        'subscription_change',
        'checkout_error',
        4,
        { from_plan: currentTier, to_plan: plan?.tier, retry_count: newRetryCount }
      );
      
      alert('Failed to start checkout. Please try again.');
    } finally {
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const handleProrationConfirm = () => {
    if (prorationData) {
      proceedToCheckout(prorationData?.plan, prorationData?.cycle);
      setShowProration(false);
      setProrationData(null);
    }
  };

  const handleAddAddon = async () => {
    if (!subscription?.user_id || addonQuantity < 1) return;

    setProcessing(true);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/manage-addons`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'add',
            quantity: addonQuantity
          })
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to add addon');
      }

      alert(`Successfully added ${addonQuantity} additional agent${addonQuantity > 1 ? 's' : ''}!`);
      setShowAddonModal(false);
      setAddonQuantity(1);
      await loadAddons();
      onPlanChange?.();
    } catch (error) {
      console.error('Add addon error:', error);
      alert('Failed to add addon. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveAddon = async (addonId) => {
    if (!confirm('Are you sure you want to remove this add-on? You will lose access at the end of your billing period.')) {
      return;
    }

    setProcessing(true);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/manage-addons`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'remove',
            addonId
          })
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to remove addon');
      }

      alert('Add-on removed successfully!');
      await loadAddons();
      onPlanChange?.();
    } catch (error) {
      console.error('Remove addon error:', error);
      alert('Failed to remove addon. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription?.id) return;

    setProcessing(true);
    const currentRetry = retryCount;
    
    try {
      const { error } = await subscriptionService?.cancelSubscription(subscription?.id);
      if (error) throw error;

      // Track subscription cancellation
      analyticsService?.trackSubscriptionChange(
        'cancel',
        currentTier,
        'free',
        -(plans?.find(p => p?.tier === currentTier)?.price || 0)
      );
      
      // If this was a retry, track successful recovery
      if (currentRetry > 0) {
        analyticsService?.trackErrorRecovery(
          'cancel_subscription_failed',
          'Cancellation succeeded after retry',
          currentRetry,
          true,
          currentTier
        );
        setRetryCount(0);
      }

      alert('Your subscription will be canceled at the end of the billing period.');
      setShowCancelModal(true);
    } catch (error) {
      const newRetryCount = currentRetry + 1;
      setRetryCount(newRetryCount);
      
      console.error('Cancel error:', error);
      
      // Track cancellation error
      analyticsService?.trackErrorRecovery(
        'cancel_subscription_failed',
        error?.message || 'Failed to cancel subscription',
        newRetryCount,
        false,
        currentTier
      );
      
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleCancelClick = () => {
    setShowCancelModal(true);
    
    // Track friction point - user considering cancellation
    analyticsService?.trackFrictionPoint(
      'subscription_management',
      'cancel_consideration',
      3,
      { current_plan: currentTier }
    );
  };

  const handleCancelConfirm = async (reason, feedback, acceptedOffer) => {
    if (!subscription?.id) return;

    setProcessing(true);
    const currentRetry = retryCount;
    
    try {
      // Submit cancellation survey
      await subscriptionService?.submitCancellationSurvey(
        subscription?.user_id,
        subscription?.id,
        reason,
        feedback,
        acceptedOffer
      );

      if (acceptedOffer) {
        // Apply retention offer
        const { data: offerData, error: offerError } = await subscriptionService?.applyRetentionOffer(subscription?.user_id);
        
        if (offerError) throw offerError;

        alert(`Great! We've applied a 20% discount to your subscription for the next 3 months. You'll save on your next billing cycle!`);
        setShowCancelModal(false);
        onPlanChange?.();
      } else {
        // Proceed with cancellation
        const { error } = await subscriptionService?.cancelSubscription(subscription?.id);
        if (error) throw error;

        // Track subscription cancellation
        analyticsService?.trackSubscriptionChange(
          'cancel',
          currentTier,
          'free',
          -(plans?.find(p => p?.tier === currentTier)?.price || 0)
        );
        
        if (currentRetry > 0) {
          analyticsService?.trackErrorRecovery(
            'cancellation_failed',
            'Cancellation succeeded after retry',
            currentRetry,
            true,
            currentTier
          );
          setRetryCount(0);
        }

        alert('Your subscription has been scheduled for cancellation at the end of your billing period.');
        setShowCancelModal(false);
        onPlanChange?.();
      }
    } catch (error) {
      const newRetryCount = currentRetry + 1;
      setRetryCount(newRetryCount);
      
      console.error('Cancellation error:', error);
      
      analyticsService?.trackErrorRecovery(
        'cancellation_failed',
        error?.message || 'Failed to cancel subscription',
        newRetryCount,
        false,
        currentTier
      );
      
      analyticsService?.trackFrictionPoint(
        'subscription_change',
        'cancellation_error',
        4,
        { plan: currentTier, retry_count: newRetryCount }
      );
      
      alert('Failed to process cancellation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePauseClick = () => {
    setShowPauseModal(true);
  };

  const handlePauseConfirm = async (durationMonths) => {
    if (!subscription?.user_id) return;

    setProcessing(true);
    try {
      const { data, error } = await subscriptionService?.pauseSubscription(
        subscription?.user_id,
        durationMonths
      );

      if (error) throw error;

      if (data?.success) {
        const pauseEndDate = new Date(data?.pause_end)?.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
        alert(`Your subscription has been paused for ${durationMonths} month${durationMonths > 1 ? 's' : ''}. It will automatically resume on ${pauseEndDate}.`);
        setShowPauseModal(false);
        onPlanChange?.();
      }
    } catch (error) {
      console.error('Pause subscription error:', error);
      alert('Failed to pause subscription. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleResumeSubscription = async () => {
    if (!subscription?.user_id) return;

    if (!confirm('Are you sure you want to resume your subscription now? Billing will restart immediately.')) {
      return;
    }

    setProcessing(true);
    try {
      const { data, error } = await subscriptionService?.resumeSubscription(subscription?.user_id);

      if (error) throw error;

      if (data?.success) {
        alert('Your subscription has been resumed successfully!');
        onPlanChange?.();
      }
    } catch (error) {
      console.error('Resume subscription error:', error);
      alert('Failed to resume subscription. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const getPlanComparison = (planTier) => {
    const tiers = ['free', 'draftiq_plus', 'enterprise'];
    const currentIndex = tiers?.indexOf(currentTier);
    const planIndex = tiers?.indexOf(planTier);

    if (planIndex > currentIndex) return 'upgrade';
    if (planIndex < currentIndex) return 'downgrade';
    return 'current';
  };

  const getPlanGradient = (tier) => {
    const gradients = {
      free: 'from-gray-500/10 to-gray-600/5',
      draftiq_plus: 'from-blue-500/10 to-blue-600/5',
      enterprise: 'from-purple-500/10 to-purple-600/5'
    };
    return gradients?.[tier] || gradients?.free;
  };

  const getDisplayPrice = (plan, cycle) => {
    if (cycle === 'annual' && plan?.annual_price) {
      return {
        price: plan?.annual_price,
        period: '/year',
        savings: plan?.annual_discount_percent
      };
    }
    return {
      price: plan?.price,
      period: '/month',
      savings: null
    };
  };

  const totalAddonCost = addons?.reduce((sum, addon) => sum + (addon?.quantity * addon?.unit_price), 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h2 className="text-xl font-semibold text-foreground mb-1 font-heading">
          Plan Management
        </h2>
        <p className="text-sm text-muted-foreground">
          Upgrade or downgrade your subscription plan
        </p>
      </div>
      {/* Billing Cycle Toggle */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Billing Cycle</h3>
            <p className="text-sm text-muted-foreground">Switch between monthly and annual billing</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'annual' : 'monthly')}
              className="relative w-14 h-7 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              style={{ backgroundColor: billingCycle === 'annual' ? 'var(--color-primary)' : 'var(--color-muted)' }}
            >
              <span
                className="absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform duration-200"
                style={{ transform: billingCycle === 'annual' ? 'translateX(28px)' : 'translateX(0)' }}
              />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Annual
            </span>
            {billingCycle === 'annual' && (
              <span className="px-2 py-1 bg-green-500/10 text-green-500 rounded-full text-xs font-semibold">
                Save up to 20%
              </span>
            )}
          </div>
        </div>
      </div>
      {/* Add-ons Section */}
      {currentTier !== 'free' && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1 font-heading flex items-center gap-2">
                <Icon name="Plus" size={20} className="text-primary" />
                Add-ons
              </h3>
              <p className="text-sm text-muted-foreground">
                Purchase additional agents beyond your plan limit ($15/agent/month)
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowAddonModal(true)}
              iconName="Plus"
              iconPosition="left"
              disabled={processing}
            >
              Add Agents
            </Button>
          </div>

          {addons?.length > 0 ? (
            <div className="space-y-3 mt-4">
              {addons?.map((addon) => (
                <div key={addon?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {addon?.quantity} Additional Agent{addon?.quantity > 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ${addon?.unit_price?.toFixed(2)}/agent/month
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-foreground">
                      ${(addon?.quantity * addon?.unit_price)?.toFixed(2)}/month
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAddon(addon?.id)}
                      iconName="Trash2"
                      disabled={processing}
                    />
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="text-sm font-medium text-muted-foreground">Total Add-on Cost</span>
                <span className="text-lg font-bold text-primary">${totalAddonCost?.toFixed(2)}/month</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No add-ons purchased yet
            </p>
          )}
        </div>
      )}
      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans?.map((plan) => {
          const comparison = getPlanComparison(plan?.tier);
          const isCurrent = comparison === 'current' && subscription?.billing_cycle === billingCycle;
          const isEnterprise = plan?.tier === 'enterprise';
          const isPremium = plan?.tier === 'draftiq_plus';
          const isProcessingThis = processing && selectedPlan === plan?.tier;
          const displayPrice = getDisplayPrice(plan, billingCycle);

          return (
            <div
              key={plan?.id}
              className={`
                bg-gradient-to-br ${getPlanGradient(plan?.tier)} rounded-xl border p-6
                transition-all duration-300
                ${isPremium ? 'border-primary scale-105' : 'border-border'}
                ${isCurrent ? 'ring-2 ring-primary' : ''}
              `}
            >
              {isPremium && (
                <div className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-semibold mb-4">
                  <Icon name="Star" size={12} />
                  Most Popular
                </div>
              )}

              {isCurrent && (
                <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded-full text-xs font-semibold mb-4">
                  <Icon name="Check" size={12} />
                  Current Plan
                </div>
              )}

              {displayPrice?.savings && (
                <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/10 text-green-500 rounded-full text-xs font-semibold mb-4">
                  Save {displayPrice?.savings}%
                </div>
              )}

              <h3 className="text-xl font-bold text-foreground mb-2 font-heading">{plan?.name}</h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-bold text-foreground">${displayPrice?.price?.toFixed(2)}</span>
                {plan?.price > 0 && <span className="text-muted-foreground text-sm">{displayPrice?.period}</span>}
              </div>

              {isEnterprise && (
                <p className="text-xs text-muted-foreground mb-4">
                  No less than $200 per seat
                </p>
              )}

              <div className="mb-6">
                <p className="text-sm text-muted-foreground mb-4">
                  Up to <span className="font-semibold text-foreground">{plan?.max_agents} agents</span>
                </p>
                <ul className="space-y-2">
                  {plan?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {isEnterprise ? (
                <div className="space-y-2">
                  <a href="tel:612-545-8283" className="block">
                    <Button
                      variant="default"
                      size="md"
                      className="w-full"
                      iconName="Phone"
                      iconPosition="left"
                    >
                      Call Sales
                    </Button>
                  </a>
                </div>
              ) : (
                <Button
                  variant={isPremium ? 'default' : 'outline'}
                  size="md"
                  className="w-full"
                  onClick={() => handleUpgrade(plan, billingCycle)}
                  disabled={isCurrent || isProcessingThis}
                >
                  {isCurrent ? 'Current Plan' : isProcessingThis ? 'Processing...' : comparison === 'upgrade' ? 'Upgrade' : comparison === 'downgrade' ? 'Downgrade' : 'Select'}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      {/* Cancel Subscription */}
      {currentTier !== 'free' && !subscription?.cancel_at_period_end && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Cancel Subscription</h3>
              <p className="text-sm text-muted-foreground">
                Cancel your subscription at any time. You'll continue to have access until the end of your billing period.
              </p>
            </div>
            <Button
              variant="danger"
              size="md"
              onClick={handleCancelClick}
              disabled={processing}
              iconName="XCircle"
              iconPosition="left"
            >
              Cancel Subscription
            </Button>
          </div>
        </div>
      )}
      {/* Pause/Resume Subscription */}
      {isPaused && currentTier !== 'free' && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 font-heading flex items-center gap-2">
            <Icon name="Play" size={20} className="text-green-500" />
            Resume Subscription
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Your subscription is currently paused. Resume to regain full access to all features.
          </p>
          <Button
            variant="default"
            size="md"
            onClick={handleResumeSubscription}
            disabled={processing}
            iconName="Play"
            iconPosition="left"
          >
            Resume Subscription
          </Button>
        </div>
      )}

      {!isPaused && currentTier !== 'free' && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 font-heading flex items-center gap-2">
            <Icon name="Pause" size={20} className="text-orange-500" />
            Pause Subscription
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Need a break? Pause your subscription for 1-3 months. Your data will be retained and billing will stop during the pause period.
          </p>
          <Button
            variant="warning"
            size="md"
            onClick={handlePauseClick}
            disabled={processing || subscription?.cancel_at_period_end}
            iconName="Pause"
            iconPosition="left"
          >
            Pause Subscription
          </Button>
        </div>
      )}
      {/* Proration Modal */}
      {showProration && prorationData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 mx-auto mb-4 flex items-center justify-center">
                <Icon name="Calculator" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2 font-heading">
                Proration Calculation
              </h3>
              <p className="text-sm text-muted-foreground">
                Your plan change will be prorated based on your current billing cycle
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-sm text-muted-foreground">Credit from current plan</span>
                <span className="text-sm font-semibold text-green-500">
                  -${prorationData?.proration_credit?.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-sm text-muted-foreground">Charge for new plan</span>
                <span className="text-sm font-semibold text-foreground">
                  +${prorationData?.proration_charge?.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 bg-muted/30 rounded-lg px-4">
                <span className="text-base font-semibold text-foreground">Net amount due today</span>
                <span className="text-xl font-bold text-primary">
                  ${Math.abs(prorationData?.net_amount || 0)?.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowProration(false);
                  setProrationData(null);
                }}
                className="flex-1 px-4 py-3 border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleProrationConfirm}
                className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity font-semibold"
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add-on Modal */}
      {showAddonModal && (
        <Modal
          isOpen={showAddonModal}
          onClose={() => setShowAddonModal(false)}
          title="Add Additional Agents"
          description="Purchase additional agents beyond your plan limit"
          type="info"
          icon="Plus"
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="addon-quantity" className="text-sm font-medium text-foreground mb-2 block">
                Number of agents to add
              </label>
              <Input
                id="addon-quantity"
                type="number"
                min="1"
                max="10"
                value={addonQuantity}
                onChange={(e) => setAddonQuantity(parseInt(e?.target?.value) || 1)}
              />
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Price per agent</span>
                <span className="text-sm font-semibold text-foreground">$15.00/month</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Total monthly cost</span>
                <span className="text-lg font-bold text-foreground">${(addonQuantity * 15)?.toFixed(2)}/month</span>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowAddonModal(false)}
                disabled={processing}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleAddAddon}
                disabled={processing}
                className="flex-1"
              >
                {processing ? 'Adding...' : 'Add Agents'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {/* Cancellation Modal */}
      <CancellationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        subscription={subscription}
        processing={processing}
      />
      {/* Pause Subscription Modal */}
      <PauseSubscriptionModal
        isOpen={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        onConfirm={handlePauseConfirm}
        subscription={subscription}
        processing={processing}
      />
    </div>
  );
};

export default PlanManagement;